<template>
    <div class="login-page">
        <!-- Animated background -->
        <div class="login-bg">
            <div class="login-bg-circle login-bg-circle-1"></div>
            <div class="login-bg-circle login-bg-circle-2"></div>
            <div class="login-bg-circle login-bg-circle-3"></div>
        </div>

        <div class="login-wrapper">
            <!-- Left: Form -->
            <div class="login-form-side">
                <div class="login-form-inner">
                    <!-- Logo -->
                    <div class="login-logo">
                        <!-- <div class="login-logo-circle">
                            <img :src="logoUrl" :style="logoStyle" alt="Logo" />
                        </div> -->
                        <div class="mainLogo">
                            <img :src="logoUrl" :style="logoStyle" alt="Logo" />
                        </div>
                        <h1 class="login-brand">Mocero One Platform</h1>
                        <p class="login-tagline">A product of Mocero Health</p>
                    </div>

                    <!-- Form -->
                    <form @submit.prevent="login" class="login-fields">
                        <div class="login-field">
                            <label for="email">User name</label>
                            <div class="login-input-wrap">
                                <input id="email" v-model="loginData.username" type="text"
                                    placeholder="Enter your user name" autocomplete="username" required />
                            </div>
                        </div>

                        <div class="login-field">
                            <label for="password">Password</label>
                            <div class="login-input-wrap">
                                <i class="pi pi-lock login-input-icon"></i>
                                <input id="password" v-model="loginData.password"
                                    :type="showPassword ? 'text' : 'password'" placeholder="Enter your password"
                                    autocomplete="current-password" required />
                                <button type="button" class="login-eye-btn" @click="showPassword = !showPassword">
                                    <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                                </button>
                            </div>
                        </div>

                        <div class="login-options">
                            <label class="login-remember">
                                <input type="checkbox" v-model="rememberMe" />
                                <span>Remember me</span>
                            </label>
                            <router-link to="/auth/forgot-password" class="login-forgot">Forgot password?</router-link>
                        </div>

                        <button type="submit" class="login-submit" :disabled="loading">
                            <i v-if="loading" class="pi pi-spin pi-spinner"></i>
                            <span v-else>Sign In</span>
                        </button>

                        <div v-if="errorMessage" class="login-error">
                            <i class="pi pi-exclamation-circle"></i>
                            {{ errorMessage }}
                        </div>

                        <p class="login-help">Need any help? <a href="#" class="login-help-link">Click Here</a></p>
                    </form>
                </div>
            </div>

            <!-- Right: Carousel (desktop only) -->
            <div class="login-slider-side">
                <div class="login-slider-content">
                    <div v-for="(slide, index) in carouselSlides" :key="index" class="login-slide"
                        :class="{ 'login-slide-active': currentSlide === index }">
                        <h2>{{ slide.title }}</h2>
                        <p>{{ slide.description }}</p>
                        <img :src="slide.image" :alt="slide.title" />
                    </div>

                    <div class="login-slider-dots">
                        <button v-for="(slide, index) in carouselSlides" :key="index"
                            :class="['login-dot', { 'login-dot-active': currentSlide === index }]"
                            @click="currentSlide = index"></button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Role selection dialog -->
        <MOPDialog v-model="showRoleDialog" modal header="Choose Role" class="role-dialog">
            <div class="roles-container">
                <div v-for="role in roles" :key="role.RoleId" class="role-card"
                    @click="selectRole(role.UserId, role.RoleId, role.RoleName, role.DeptRoleMapId)">
                    <i class="pi pi-user role-icon"></i>
                    <span class="role-name">{{ role.RoleName }}</span>
                </div>
            </div>
        </MOPDialog>

        <!-- Loading overlay -->
        <div v-if="loading" class="login-loading-overlay">
            <MOPProgressSpinner />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import type { LoginResult, UserData, UserDataToStore, RolesData } from '../../types/auth'
import LoginService from '../../services/auth/LoginService'
import sliderImage1 from '../../../../MOPUI/src/assets/Icons/CommonIcons/slider-1.png';
import sliderImage2 from '../../../../MOPUI/src/assets/Icons/CommonIcons/slider-2.png';
import sliderImage3 from '../../../../MOPUI/src/assets/Icons/CommonIcons/slider-3.png';
import bundledLogo from '../../assets/Img/Logo/MoceroLogo.png';
import { getLogoUrl, getLogoWidth, getLogoHeight } from '../../config';

defineOptions({
    name: 'LoginView'
})

// Logo path and size are maintained in public/config.js; falls back to the bundled asset.
const logoUrl = getLogoUrl(bundledLogo)
const logoStyle = { width: getLogoWidth(), height: getLogoHeight() }

const router = useRouter()
const authStore = useAuthStore()
const loading = ref(false)
const errorMessage = ref('')
const showRoleDialog = ref(false)
const showPassword = ref(false)
const rememberMe = ref(false)
const roles = ref<LoginResult[]>([])
const loginmsgdata = ref({
    loginmsg: '',
    class: ''
})
const loginData = ref({
    username: '',
    password: ''
})

// Carousel data and state
const currentSlide = ref(0)
const carouselSlides = ref([
    {
        title: 'Modern Healthcare Platform',
        description: 'A comprehensive solution for managing healthcare operations efficiently.',
        image: sliderImage1
    },
    {
        title: 'Advanced Patient Care',
        description: 'Streamline patient management with our intuitive healthcare tools.',
        image: sliderImage2
    },
    {
        title: 'Secure & Reliable',
        description: 'Built with enterprise-grade security and reliability in mind.',
        image: sliderImage3
    }
])

let carouselInterval: number | null = null

const nextSlide = () => {
    currentSlide.value = (currentSlide.value + 1) % carouselSlides.value.length
}

const startCarouselAutoplay = () => {
    carouselInterval = window.setInterval(() => {
        nextSlide()
    }, 5000)
}

const stopCarouselAutoplay = () => {
    if (carouselInterval) {
        clearInterval(carouselInterval)
        carouselInterval = null
    }
}

onMounted(() => {
    errorMessage.value = ''
    loginmsgdata.value.class = ''
    startCarouselAutoplay()
})

onUnmounted(() => {
    stopCarouselAutoplay()
})

const isLoginResult = (item: unknown): item is LoginResult => {
    return typeof item === 'object' && item !== null && 'RoleId' in item
}

const login = async () => {
    try {
        loading.value = true
        errorMessage.value = ''

        localStorage.removeItem('token')
        localStorage.removeItem('UsersData')
        localStorage.removeItem('RolesData')
        authStore.clearAuth()

        const response = await LoginService.GetRoles(loginData.value.username, loginData.value.password)
        console.log('Login response:', response)

        if (Array.isArray(response.result)) {
            if (response.result.length === 0) {
                errorMessage.value = 'No roles found'
                loginmsgdata.value.class = 'error'
                return
            }

            const firstResult = response.result[0]
            if (typeof firstResult === 'string') {
                if (firstResult === 'AccountLocked') {
                    // 4 consecutive failed attempts — server has flipped
                    // IsLocked=1 on UserMaster. Only an HR/Admin can clear
                    // this from the User Master page.
                    errorMessage.value = 'Account is locked after 4 failed sign-in attempts. Please contact your HR / admin to unblock.'
                    loginmsgdata.value.class = 'error'
                } else if (firstResult === 'Invalid') {
                    errorMessage.value = 'Invalid username or password'
                    loginmsgdata.value.class = 'error'
                }
                return
            }

            if (isLoginResult(firstResult)) {
                if (response.result.length > 1) {
                    roles.value = response.result.filter(isLoginResult)
                    showRoleDialog.value = true
                } else {
                    await selectRole(firstResult.UserId, firstResult.RoleId, firstResult.RoleName, firstResult.DeptRoleMapId)
                }
            }
        }
    } catch (error) {
        console.error('Login error:', error)
        errorMessage.value = 'An error occurred during login'
        loginmsgdata.value.class = 'error'
    } finally {
        loading.value = false
    }
}

const selectRole = async (userId: number, roleId: number, roleName: string, deptRoleMapId: number) => {
    try {
        loading.value = true
        showRoleDialog.value = false
        const userResponse = await LoginService.Login(userId, 1)
        console.log('User response:', userResponse)

        if (!userResponse.result || !Array.isArray(userResponse.result) || userResponse.result.length === 0) {
            throw new Error('Invalid user data received')
        }

        const userData: UserData = userResponse.result[0]
        console.log('User data:', userData)

        // Generate token from user data
        const token = btoa(JSON.stringify({
            userId,
            roleId,
            organizationId: userData.OrganizationId,
            timestamp: new Date().getTime()
        }))

        // Update auth store
        authStore.setToken(token)
        authStore.setUser({
            id: userId,
            organizationId: userData.OrganizationId,
            roleId: roleId,
            deptRoleMapId: deptRoleMapId,
            email: loginData.value.username,
            name: userData.UserName,
            branchId: userData.BranchId,
            isAdmin: userData.IsAdmin == 1 ? true : false
        })
        authStore.setEntityId(0)
        authStore.setOrganizationId(userData.OrganizationId)
        authStore.setBranchId(userData.BranchId)

        // Store in localStorage
        localStorage.setItem('UsersData', JSON.stringify(userData))
        localStorage.setItem('token', token)
        localStorage.setItem('RolesData', JSON.stringify({
            RoleId: roleId,
            RoleName: roleName,
            DeptRoleMapId: deptRoleMapId
        }))

        console.log('Auth store updated, navigating to dashboard')
        router.push('/dashboard')
    } catch (error) {
        console.error('Role selection error:', error)
        errorMessage.value = 'An error occurred while processing your login'
        loginmsgdata.value.class = 'error'
    } finally {
        loading.value = false
    }
}
</script>

<style scoped>
/* ─── Page ────────────────────────────────────────────────────────────────── */
.login-page {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f4f8;
    position: relative;
    overflow: hidden;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ─── Animated background blobs ──────────────────────────────────────────── */
.login-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    overflow: hidden;
}

.login-bg-circle {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.35;
    animation: float 20s ease-in-out infinite;
}

.login-bg-circle-1 {
    width: 500px;
    height: 500px;
    background: #0d9488;
    top: -120px;
    left: -100px;
}

.login-bg-circle-2 {
    width: 400px;
    height: 400px;
    background: #6366f1;
    bottom: -80px;
    right: -60px;
    animation-delay: -7s;
}

.login-bg-circle-3 {
    width: 300px;
    height: 300px;
    background: #f59e0b;
    top: 50%;
    left: 50%;
    animation-delay: -14s;
}

@keyframes float {

    0%,
    100% {
        transform: translate(0, 0) scale(1);
    }

    33% {
        transform: translate(30px, -30px) scale(1.05);
    }

    66% {
        transform: translate(-20px, 20px) scale(0.95);
    }
}

/* ─── Card wrapper ───────────────────────────────────────────────────────── */
.login-wrapper {
    position: relative;
    z-index: 1;
    display: flex;
    width: 960px;
    max-width: 95vw;
    min-height: 560px;
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

/* ─── Left: Form ─────────────────────────────────────────────────────────── */
.login-form-side {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 40px;
}

.login-form-inner {
    width: 100%;
    max-width: 380px;
}

/* Logo */
.login-logo {
    text-align: center;
    /* margin-bottom: 32px; */
}

.mainLogo {
     display: inline-flex;
    align-items: center;
    justify-content: center;
    /* margin-bottom: 16px;  */

    img {
        object-fit: contain;
    }
}

.login-logo-circle {
    width: 72px;
    height: 72px;
    border-radius: 18px;
    background: linear-gradient(135deg, #e6f7f6, #d1fae5);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.15);
}

.login-logo-circle img {
    width: 42px;
    height: 42px;
    object-fit: contain;
}

.login-brand {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px;
}

.login-tagline {
    font-size: 13px;
    color: #94a3b8;
    margin: 0;
}

/* Fields */
.login-fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.login-field label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;
}

.login-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.login-input-icon {
    position: absolute;
    left: 14px;
    color: #94a3b8;
    font-size: 15px;
    pointer-events: none;
}

.login-input-wrap input {
    width: 100%;
    padding: 12px 14px 12px 42px;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-size: 14px;
    color: #0f172a;
    background: #f8fafc;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.login-input-wrap input:focus {
    border-color: #0d9488;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
    background: #fff;
}

.login-input-wrap input::placeholder {
    color: #cbd5e1;
}

.login-eye-btn {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
    font-size: 15px;
}

/* Options row */
.login-options {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: -4px;
}

.login-remember {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #64748b;
    cursor: pointer;
}

.login-remember input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #0d9488;
    cursor: pointer;
}

.login-forgot {
    font-size: 13px;
    color: #0d9488;
    text-decoration: none;
    font-weight: 500;
    white-space: nowrap;
}

.login-forgot:hover {
    text-decoration: underline;
}

/* Submit */
.login-submit {
    width: 100%;
    padding: 13px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #0d9488, #0f766e);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 4px 14px rgba(13, 148, 136, 0.3);
}

.login-submit:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(13, 148, 136, 0.4);
}

.login-submit:active {
    transform: translateY(0);
}

.login-submit:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
}

/* Error */
.login-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 10px;
    background: #fef2f2;
    color: #dc2626;
    font-size: 13px;
    border: 1px solid #fecaca;
}

/* Help */
.login-help {
    text-align: center;
    font-size: 13px;
    color: #94a3b8;
    margin: 0;
}

.login-help-link {
    color: #0d9488;
    text-decoration: none;
    font-weight: 500;
}

.login-help-link:hover {
    text-decoration: underline;
}

/* ─── Right: Slider ──────────────────────────────────────────────────────── */
.login-slider-side {
    flex: 1;
    background: linear-gradient(145deg, #0d9488, #065f46);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    position: relative;
    overflow: hidden;
}

.login-slider-side::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    top: -60px;
    right: -60px;
}

.login-slider-side::after {
    content: '';
    position: absolute;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.04);
    bottom: -40px;
    left: -40px;
}

.login-slider-content {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 360px;
    text-align: center;
}

.login-slide {
    display: none;
    flex-direction: column;
    align-items: center;
    animation: fadeIn 0.5s ease;
}

.login-slide-active {
    display: flex;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.login-slide h2 {
    font-size: 22px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 8px;
}

.login-slide p {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    margin: 0 0 28px;
    line-height: 1.5;
}

.login-slide img {
    width: 280px;
    height: 280px;
    object-fit: contain;
}

.login-slider-dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;
}

.login-dot {
    width: 8px;
    height: 8px;
    border-radius: 4px;
    border: none;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    transition: all 0.3s;
}

.login-dot-active {
    width: 28px;
    background: #fff;
}

/* ─── Loading overlay ────────────────────────────────────────────────────── */
.login-loading-overlay {
    position: fixed;
    inset: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
}

/* ─── Role dialog ────────────────────────────────────────────────────────── */
.role-dialog {
    max-width: 500px;
}

.roles-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
    padding: 16px;
}

.role-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 20px 16px;
    border-radius: 14px;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s;
}

.role-card:hover {
    border-color: #0d9488;
    background: #e6f7f6;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.1);
}

.role-icon {
    font-size: 24px;
    color: #0d9488;
}

.role-name {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
}

/* ─── Responsive: Tablet ─────────────────────────────────────────────────── */
@media screen and (max-width: 900px) {
    .login-slider-side {
        display: none;
    }

    .login-wrapper {
        width: 440px;
        max-width: 95vw;
        min-height: auto;
    }
}

/* ─── Responsive: Mobile ─────────────────────────────────────────────────── */
@media screen and (max-width: 480px) {
    .login-page {
        align-items: flex-start;
        padding: 0;
        background: #fff;
    }

    .login-bg {
        display: none;
    }

    .login-wrapper {
        width: 100%;
        max-width: 100%;
        min-height: 100dvh;
        border-radius: 0;
        box-shadow: none;
    }

    .login-form-side {
        padding: 40px 24px;
        align-items: flex-start;
        padding-top: 60px;
    }

    .login-form-inner {
        max-width: 100%;
    }

    .login-logo-circle {
        width: 64px;
        height: 64px;
        border-radius: 16px;
    }

    .login-logo-circle img {
        width: 36px;
        height: 36px;
    }

    .login-brand {
        font-size: 20px;
    }

    .login-submit {
        padding: 14px;
        font-size: 16px;
    }

    .login-input-wrap input {
        padding: 14px 14px 14px 42px;
        font-size: 16px;
    }
}
</style>
